import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Alert,
  Typography,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  PersonOutline as PersonIcon,
  LockOutlined as LockIcon,
  CheckCircleOutline as CheckIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import api from '../services/api';
import { BRAND } from '../config/brand';
import BrandLogo from '../components/BrandLogo';

interface LoginProps {
  onLoginSuccess: (token: string, user: any) => void;
}

const SYSTEM_INTRO =
  '面向企业内部的知识中台：文档按部门权限入库，支持关键词与语义检索、基于知识库的智能问答，并可生成公众号稿件。';

const SYSTEM_HIGHLIGHTS = [
  { title: '权限可控', desc: '部门隔离与角色权限，知识按范围可见' },
  { title: '检索双路', desc: '上传解析入库，关键词 / 语义检索' },
  { title: '问答出稿', desc: '智能问答有据可查，公众号写作可出稿' },
] as const;

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/auth/login', formData);
      const { token, username, fullName, role, userId, departments } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify({ username, fullName, role, userId, departments }));
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      onLoginSuccess(token, { username, fullName, role, userId, departments });
      navigate('/');
    } catch (err: any) {
      console.error('登录失败:', err);
      setError(err.response?.data?.message || '登录失败，请检查用户名和密码');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: 'username' | 'password') => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const canSubmit = Boolean(formData.username.trim() && formData.password && !loading);

  const fieldSx = {
    mb: 2.25,
    '& .MuiOutlinedInput-root': {
      bgcolor: '#FFFFFF',
      borderRadius: 2,
      transition: 'box-shadow 0.2s ease',
      '&.Mui-focused': {
        boxShadow: `0 0 0 3px ${alpha('#2563EB', 0.12)}`,
      },
    },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        '@keyframes loginFadeUp': {
          from: { opacity: 0, transform: 'translateY(10px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      {/* 左：品牌介绍（主流后台常用品牌色面板） */}
      <Box
        sx={{
          position: 'relative',
          flex: { md: '0 0 46%' },
          minHeight: { xs: 280, md: '100vh' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          px: { xs: 3.5, sm: 5, md: 7 },
          py: { xs: 4, md: 6 },
          color: '#FFFFFF',
          overflow: 'hidden',
          background: 'linear-gradient(155deg, #0F172A 0%, #1E3A8A 42%, #2563EB 100%)',
        }}
      >
        {/* 氛围层：网格 + 光晕，避免纯平色 */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `
              radial-gradient(ellipse 70% 50% at 20% 20%, ${alpha('#93C5FD', 0.22)}, transparent 55%),
              radial-gradient(ellipse 50% 40% at 85% 75%, ${alpha('#1D4ED8', 0.45)}, transparent 50%),
              linear-gradient(${alpha('#FFFFFF', 0.04)} 1px, transparent 1px),
              linear-gradient(90deg, ${alpha('#FFFFFF', 0.04)} 1px, transparent 1px)
            `,
            backgroundSize: 'auto, auto, 28px 28px, 28px 28px',
            pointerEvents: 'none',
          }}
        />

        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            maxWidth: 440,
            mx: { xs: 'auto', md: 0 },
            '@media (prefers-reduced-motion: no-preference)': {
              animation: 'loginFadeUp 0.55s ease both',
            },
          }}
        >
          <Stack direction="row" spacing={1.75} alignItems="center" sx={{ mb: 4 }}>
            <BrandLogo
              size={48}
              title={BRAND.shortName}
              sx={{ boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}
            />
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.2,
                }}
              >
                {BRAND.shortName}
              </Typography>
              <Typography sx={{ mt: 0.4, fontSize: '0.8125rem', color: alpha('#FFF', 0.72) }}>
                企业知识中台
              </Typography>
            </Box>
          </Stack>

          <Typography
            component="h2"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.5rem', md: '1.75rem' },
              letterSpacing: '-0.03em',
              lineHeight: 1.3,
              mb: 1.5,
            }}
          >
            让企业知识
            <br />
            可搜、可问、可复用
          </Typography>

          <Typography
            sx={{
              fontSize: '0.875rem',
              lineHeight: 1.7,
              color: alpha('#FFF', 0.78),
              mb: 3.5,
              maxWidth: 380,
            }}
          >
            {SYSTEM_INTRO}
          </Typography>

          <Stack spacing={1.5}>
            {SYSTEM_HIGHLIGHTS.map((item) => (
              <Box
                key={item.title}
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'flex-start',
                  px: 1.75,
                  py: 1.5,
                  borderRadius: 2,
                  bgcolor: alpha('#FFFFFF', 0.08),
                  border: `1px solid ${alpha('#FFFFFF', 0.12)}`,
                  backdropFilter: 'blur(6px)',
                }}
              >
                <CheckIcon sx={{ fontSize: 20, mt: 0.15, color: '#93C5FD' }} />
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.3 }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ mt: 0.35, fontSize: '0.75rem', color: alpha('#FFF', 0.7), lineHeight: 1.5 }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>

      {/* 右：登录表单 */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#F8FAFC',
          minHeight: { xs: 'auto', md: '100vh' },
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 2.5, sm: 4 },
            py: { xs: 4, md: 6 },
          }}
        >
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{
              width: '100%',
              maxWidth: 400,
              '@media (prefers-reduced-motion: no-preference)': {
                animation: 'loginFadeUp 0.55s ease 0.08s both',
              },
            }}
          >
            <Typography
              component="h1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.5rem', sm: '1.625rem' },
                letterSpacing: '-0.02em',
                color: 'text.primary',
                mb: 0.75,
              }}
            >
              欢迎登录
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3.5, lineHeight: 1.6 }}>
              使用企业账号进入{BRAND.shortName}管理后台
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <TextField
              margin="none"
              required
              fullWidth
              id="username"
              label="用户名"
              name="username"
              autoComplete="username"
              autoFocus
              value={formData.username}
              onChange={handleInputChange('username')}
              disabled={loading}
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              margin="none"
              required
              fullWidth
              name="password"
              label="密码"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleInputChange('password')}
              disabled={loading}
              sx={{ ...fieldSx, mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? '隐藏密码' : '显示密码'}
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      size="small"
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={!canSubmit}
              sx={{
                py: 1.35,
                fontWeight: 600,
                fontSize: '0.9375rem',
                borderRadius: 2,
                boxShadow: '0 8px 20px rgba(37, 99, 235, 0.28)',
                '&:hover': {
                  boxShadow: '0 10px 24px rgba(37, 99, 235, 0.34)',
                },
              }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : '登录'}
            </Button>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 2.75, textAlign: 'center', fontSize: '0.8125rem' }}
            >
              需要开通账号？请联系系统管理员
            </Typography>
          </Box>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            textAlign: 'center',
            pb: 2.5,
            px: 2,
            fontSize: '0.75rem',
          }}
        >
          {BRAND.copyright}
        </Typography>
      </Box>
    </Box>
  );
};

export default Login;
